import { workflowRunPath } from '@/constants/workflow-editor';
import { extractOrgFromPath, orgPath } from '@/utils/org-path';
import {
  NotificationEventType,
  NotificationItem,
} from '@/types/notification-types';

export type NotificationTargetKind = 'run' | 'workflow' | 'link';

export interface NotificationTarget {
  kind: NotificationTargetKind;
  path: string;
}

// Picking a file up and failing to process it are reports on the trigger's own
// work: the runs that did start are announced separately, so these two have
// nowhere useful to send anyone.
const UNTARGETED_EVENT_TYPES: NotificationEventType[] = [
  'trigger_files_detected',
  'trigger_files_failed',
];

// Protocol-relative ("//host") is neither an in-app path nor a URL we can vet.
const isInternalPath = (link: string): boolean =>
  link.startsWith('/') && !link.startsWith('//');

export const resolveNotificationTarget = (
  notification: NotificationItem,
): NotificationTarget | null => {
  if (UNTARGETED_EVENT_TYPES.includes(notification.event_type)) return null;
  const runId = notification.metadata?.run_id as string | undefined;
  const workflowId = notification.metadata?.workflow_id as string | undefined;
  if (workflowId) {
    return {
      kind: runId ? 'run' : 'workflow',
      path: orgPath(workflowRunPath(workflowId, runId)),
    };
  }
  const link = notification.link;
  if (!link) return null;
  if (link.startsWith('//')) return null;
  if (!isInternalPath(link)) return { kind: 'link', path: link };
  return {
    kind: 'link',
    path: extractOrgFromPath(link) ? link : orgPath(link),
  };
};

export const getNotificationTarget = (
  notification: NotificationItem,
): string | null => resolveNotificationTarget(notification)?.path ?? null;
